/**
 * De donde saca el panel el HTML de una página.
 *
 * El marco aislado no tiene origen propio, así que no puede pedir el
 * documento por su cuenta: lo pide el panel con la sesión que corresponda y
 * se lo entrega ya cargado. Como cada documento se identifica por su huella,
 * nunca cambia, y basta con pedirlo una vez.
 */
import { pb, pbApp } from "./pb";
import { inlinePlaneAssets } from "./planeAssets";

export type DocLoader = (hash: string) => Promise<string>;

/*
 * Los documentos ya pedidos, de más viejo a más reciente.
 *
 * Con tope: cada uno lleva dentro la hoja de estilos, el puente y las
 * gráficas --más de medio mega aunque la página sea corta-- y cada vez que la
 * IA reescribe una página nace una huella nueva. Sin tope, una tarde de
 * trabajo dejaba cientos de megas en la pestaña. Lo que se cae se vuelve a
 * pedir, y el servidor contesta que no cambió.
 */
const cache = new Map<string, Promise<string>>();
const MAX_CACHED_DOCS = 24;

/**
 * Olvida los documentos ya pedidos. Llevan dentro el CSS y el JavaScript
 * comunes, así que al guardarlos hay que volver a pedirlos.
 */
export function forgetDocs(): void {
  cache.clear();
}

/** Guarda un documento como el más reciente y suelta los más viejos. */
function remember(key: string, pending: Promise<string>) {
  cache.delete(key);
  cache.set(key, pending);
  for (const old of cache.keys()) {
    if (cache.size <= MAX_CACHED_DOCS) break;
    cache.delete(old);
  }
}

/**
 * `dentro`: el documento va a dibujarse en el marco, así que los archivos de
 * Planer se le ponen dentro --el marco no tiene origen con el que pedirlos, ver
 * `planeAssets.ts`--. Sin esto el código se sirve tal como se guardó, que es
 * lo que necesita el editor.
 */
function fetchDoc(url: string, token?: string, key = url, dentro = false): Promise<string> {
  const cached = cache.get(key);
  if (cached) {
    // Vuelve al final de la fila: es el último que se pidió.
    remember(key, cached);
    return cached;
  }

  const pending = (async () => {
    const headers: Record<string, string> = {};
    if (token) headers.authorization = token;
    const res = await fetch(url, { headers });
    if (!res.ok) {
      const detail = await res.json().catch(() => null);
      throw new Error(detail?.error ?? "No se pudo cargar el contenido");
    }
    const html = await res.text();
    return dentro ? inlinePlaneAssets(html) : html;
  })();

  // Un fallo no se guarda: la próxima vez se vuelve a intentar.
  pending.catch(() => {
    if (cache.get(key) === pending) cache.delete(key);
  });
  remember(key, pending);
  return pending;
}

/*
 * Los cargadores ya hechos, por lo que piden.
 *
 * Cada pantalla deriva el suyo de la aplicación y la página que tiene
 * abiertas, y esas se vuelven a leer a menudo --al terminar la IA, al acabar
 * la pasada de memoria, cuando otra conversación cambia algo--: llegan como
 * objetos nuevos aunque digan lo mismo. Un cargador nuevo es, para el marco,
 * otro origen del documento, y lo vuelve a montar: la página se recargaba
 * mientras alguien la usaba y se perdía lo escrito en un formulario. Con el
 * mismo cargador para lo mismo, el marco solo se recarga si cambia la huella.
 */
const loaders = new Map<string, DocLoader>();
const MAX_LOADERS = 128;

function loaderFor(key: string, make: () => DocLoader): DocLoader {
  let loader = loaders.get(key);
  if (!loader) {
    loader = make();
  }
  // Se conservan los recientes sin acumular todas las páginas visitadas en la pestaña.
  loaders.delete(key);
  loaders.set(key, loader);
  for (const old of loaders.keys()) {
    if (loaders.size <= MAX_LOADERS) break;
    loaders.delete(old);
  }
  return loader;
}

/*
 * El HTML de una página se pide por la página, no por su huella: lo que se
 * sirve lleva dentro las dos referencias inyectadas, que no forman parte del
 * documento guardado. La huella sigue haciendo falta para la memoria: cuando
 * cambia, hay que volver a pedirlo.
 */

/** El HTML de una página, con la sesión del constructor. */
export const appPageLoader = (appId: string, pageId: string): DocLoader =>
  loaderFor(
    `app:${appId}:${pageId}`,
    () => (hash) =>
      fetchDoc(
        `/api/apps/${appId}/paginas/${pageId}/html`,
        pb.authStore.token,
        `app:${appId}:${pageId}:${hash}`,
        true,
      ),
  );

/**
 * Para la vista previa: por huella, no por página.
 *
 * Pedir por página siempre trae el borrador, que es justo lo que no se quiere
 * al mirar una versión guardada. La fotografía trae la huella de entonces y se
 * pide esa; el borrador también pasa por aquí, con la huella que tiene ahora.
 */
export const previewPageLoader = (appId: string, pageId: string): DocLoader =>
  loaderFor(
    `vista:${appId}:${pageId}`,
    () => (hash) =>
      fetchDoc(
        `/api/apps/${appId}/paginas/${pageId}/html/${hash}`,
        pb.authStore.token,
        `vista:${appId}:${pageId}:${hash}`,
        true,
      ),
  );

/** El HTML tal como se guardó, que es lo que edita el constructor. */
export const rawPageLoader = (appId: string, pageId: string): DocLoader =>
  loaderFor(
    `crudo:${appId}:${pageId}`,
    () => (hash) =>
      fetchDoc(
        `/api/apps/${appId}/paginas/${pageId}/html/crudo`,
        pb.authStore.token,
        `crudo:${appId}:${pageId}:${hash}`,
      ),
  );

/** El HTML de una página publicada, con la sesión de quien la está usando. */
export const publicPageLoader = (slug: string, pageId: string): DocLoader =>
  loaderFor(
    `pub:${slug}:${pageId}`,
    () => (hash) =>
      fetchDoc(
        `/api/public/${encodeURIComponent(slug)}/paginas/${pageId}/html`,
        pbApp.authStore.token,
        `pub:${slug}:${pageId}:${hash}`,
        true,
      ),
  );
